import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Plus, Umbrella, Trash2, Edit, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function LeaveTypes() {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    is_paid: false,
    pay_percentage: 0,
    max_days: '',
    description: ''
  });

  useEffect(() => { fetchLeaveTypes(); }, []);

  const fetchLeaveTypes = async () => {
    try {
      const res = await api.get('/leave-types');
      setLeaveTypes(res.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        pay_percentage: form.is_paid ? parseFloat(form.pay_percentage) : 0,
        max_days: form.max_days ? parseInt(form.max_days) : null
      };
      if (editMode) {
        await api.put(`/leave-types/${selectedId}`, payload);
        toast.success('Type de conge modifie');
      } else {
        await api.post('/leave-types', payload);
        toast.success('Type de conge cree');
      }
      setOpen(false);
      resetForm();
      fetchLeaveTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (lt) => {
    setEditMode(true);
    setSelectedId(lt.id);
    setForm({
      name: lt.name,
      is_paid: lt.is_paid,
      pay_percentage: lt.pay_percentage || 0,
      max_days: lt.max_days ? lt.max_days.toString() : '',
      description: lt.description || ''
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce type de conge ?')) return;
    try {
      await api.delete(`/leave-types/${id}`);
      toast.success('Type de conge supprime');
      fetchLeaveTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const resetForm = () => {
    setForm({ name: '', is_paid: false, pay_percentage: 0, max_days: '', description: '' });
    setEditMode(false);
    setSelectedId(null);
  };

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="leave-types-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Types de Conge</h1>
          <p className="text-slate-600 mt-1">Gerez les types de conge disponibles</p>
        </div>
        <Button onClick={() => { resetForm(); setOpen(true); }} data-testid="add-leave-type-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un type
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {leaveTypes.filter(lt => lt.is_active !== false).map(lt => (
          <Card key={lt.id} className={`border ${lt.is_default ? 'border-green-300 bg-green-50/30' : 'border-slate-200'}`} data-testid={`leave-type-${lt.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Umbrella className="w-4 h-4 text-green-600" />
                  {lt.name}
                </CardTitle>
                {lt.is_default && <Badge className="bg-green-100 text-green-700 border-green-200" variant="outline">Par defaut</Badge>}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {lt.is_paid ? (
                    <Badge className="bg-blue-100 text-blue-700">Paye {lt.pay_percentage}%</Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500">Non paye</Badge>
                  )}
                  {lt.max_days && <Badge variant="outline">{lt.max_days} jours max</Badge>}
                  {!lt.max_days && <Badge variant="outline" className="text-slate-400">Illimite</Badge>}
                </div>
                {lt.description && <p className="text-sm text-slate-500 mt-2">{lt.description}</p>}
                {!lt.is_default && (
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(lt)} data-testid={`edit-${lt.id}`}>
                      <Edit className="w-3 h-3 mr-1" /> Modifier
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(lt.id)} data-testid={`delete-${lt.id}`}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier' : 'Nouveau'} Type de Conge</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du type de conge *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Ex: Conge maladie" data-testid="lt-name-input" />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <div>
                <p className="font-medium text-sm">Conge paye ?</p>
                <p className="text-xs text-slate-500">L'employe recoit-il un salaire pendant ce conge</p>
              </div>
              <Switch checked={form.is_paid} onCheckedChange={(val) => setForm({ ...form, is_paid: val })} data-testid="lt-paid-switch" />
            </div>

            {form.is_paid && (
              <div className="space-y-2 pl-4 border-l-2 border-blue-300">
                <Label>Pourcentage paye (%)</Label>
                <Input type="number" min="0" max="100" value={form.pay_percentage} onChange={(e) => setForm({ ...form, pay_percentage: e.target.value })} data-testid="lt-percentage-input" />
                <p className="text-xs text-slate-500">100% = salaire complet, 50% = demi-salaire</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Duree maximum (jours)</Label>
              <Input type="number" min="1" value={form.max_days} onChange={(e) => setForm({ ...form, max_days: e.target.value })} placeholder="Vide = illimite" data-testid="lt-maxdays-input" />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Description optionnelle..." data-testid="lt-description-input" />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">Annuler</Button>
              <Button type="submit" className="flex-1" data-testid="lt-submit-btn">{editMode ? 'Modifier' : 'Creer'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
